/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import Highcharts from 'highcharts-pack/highcharts/highcharts';
import { ILinkOptions } from 'src/adhoc/store/types/StateTypes';
import datasetTypes from '../../enums/datasetTypes';
import GenericChartVisualization from './chart/GenericChartVisualization';
import genericChartVisualizationStaticPropertiesReducer from './chart/reducers/genericChartVisualizationReducer';
import { getEvents, getOutputParamsKeyFromField, getOutputParamFromMeasure } from './chart/util/EventUtil';
import {
    getMeasures,
    getTooltipValue
} from './chart/util/GenericSeriesUtil';
import i18n from '../../../i18n/AdHocBundle.properties';

export const SEPARATOR = '/@#/';

const exploreLevel = (level: any[], data: any[], isRootLevel: boolean): { localMin: number | undefined, localMax: number | undefined } => {
    let localMin: number | undefined,
        localMax: number | undefined;
    const colorPalette = Highcharts.getOptions().colors;
    let colorIndex = 0;
    level.forEach((levelResult: any) => {
        const levelData = { ...levelResult };
        if (levelData.value) {
            if (isRootLevel) {
                levelData.color = colorPalette[colorIndex % colorPalette.length]
            }
            data.push(levelData);
            if (!localMin || levelData.value < localMin) {
                localMin = levelData.value;
            }
            if (!localMax || levelData.value > localMax) {
                localMax = levelData.value;
            }
            colorIndex += 1;
        }
    });
    return { localMin, localMax };
}

export class TreeMapVisualization extends GenericChartVisualization {

    useShadow = false

    getChartSection() {
        const chart = super.getChartSection();

        return {
            ...chart,
            type: 'treemap',
            renderTo: 'chartContainer',
            zoomType: 'xy',
            marginTop: 0
        };
    }

    // eslint-disable-next-line class-methods-use-this
    getLegendSection() {
        return {
            enabled: false
        };
    }

    getTooltipSection() {

        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);

        const getTooltipProps = {
            // there is only one measure always, so we don't have to worry about finding the right measure id
            measures,
            defaultFieldAggregationType: this.defaultFieldAggregationType,
            defaultFieldAggregationFormat: this.defaultFieldAggregationFormat
        };

        return {
            ...super.getTooltipSection(),
            valueDecimals: 2,
            formatter() {
                const chartPoint: any = this;
                const measurePrefix = '<span style="color:{point.color}">\u25CF</span>';
                const tooltipValue = getTooltipValue(getTooltipProps, chartPoint.point.value);

                if (chartPoint.point.tooltipLabel) {
                    return `${chartPoint.point.tooltipLabel}</br>${measurePrefix} ${chartPoint.point.measureLabel}: <b>${tooltipValue}</b>`;
                }
                return `${measurePrefix} ${chartPoint.point.measureLabel}: <b>${tooltipValue}</b>`
            },
            useHTML: true,
            style: {
                fontSize: '1em'
            }
        }
    }

    getPlotOptionsSection() {
        const section = super.getPlotOptionsSection();
        return {
            ...section,
            series: {
                ...section.series,
                dataLabels: {
                    overflow: 'none'
                }
            }
        };
    }

    getSeriesSection(selectedColIndex: number, selectedRowIndex: number): any {
        const dataset = this.getDataset();
        const linkOptions :ILinkOptions = this.getLinkOptions();
        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);
        const measure = measures[0];
        const levels = dataset.summary.rows.items.length;
        const results: any[][] = new Array(levels);

        let firstMinRecorded = false,
            firstMaxRecorded = false,
            min :number = 0,
            max :number = 0;
        for (let i = 0; i < levels; i += 1) {
            results[i] = [];
        }

        dataset.data.rows.children.forEach((child: any) => {
            this.exploreSeries(results, 0, child, [], [], dataset.data.aggregations, measure, dataset);
        });

        const data: any[] = [];
        if (selectedRowIndex === 0) {
            const value = parseFloat(dataset.data.aggregations[0][0]);
            min = value;
            max = value;
            data.push({
                id: `${SEPARATOR}${i18n['adhoc.node.total.node']}`,
                name: i18n['adhoc.node.total.node'],
                measureLabel: measure.label,
                tooltipLabel: i18n['adhoc.node.total.node'],
                color: Highcharts.getOptions().colors[0],
                value,
                linkOutputParams: {
                    // the TreeMap has always one measure
                    measures: [getOutputParamFromMeasure(measure)]
                }
            });
        } else {
            for (let i = selectedRowIndex - 1; i >= 0; i -= 1) {
                const level = results[i];
                const { localMin, localMax } = exploreLevel(level, data, i === 0);
                if (localMin) {
                    if (!firstMinRecorded) {
                        min = localMin;
                        firstMinRecorded = true;
                    } else {
                        min = Math.min(localMin, min);
                    }
                }
                if (localMax) {
                    if (!firstMaxRecorded) {
                        max = localMax;
                        firstMaxRecorded = true;
                    } else {
                        max = Math.max(localMax, max);
                    }
                }
            }
        }

        this.recordMinMaxValues(min, max);

        return [
            {
                borderWidth: 1,
                dataLabels: {
                    enabled: false
                },
                name: staticProperties.levelsStructure.rows[0].label,
                cursor: 'pointer',
                data,
                point: {
                    events: getEvents(linkOptions)
                },
                layoutAlgorithm: 'squarified',
                chartType: 'treemap',
                allowTraversingTree: true,
                levels: [
                    {
                        level: 1,
                        dataLabels: {
                            enabled: true
                        },
                        borderWidth: 3
                    }
                ],
                levelIsConstant: false
            }
        ];
    }

    getTotalIndex(currentTotalNode: any): number {
        if (currentTotalNode.index !== -1) {
            return currentTotalNode.index;
        }
        if (currentTotalNode.total) {
            return this.getTotalIndex(currentTotalNode.total);
        }
        return -1;
    }

    exploreSeries(results: any[][], level: number, currentNode: any, path: string[], rowNames: string[], aggregations: any[], measure: any, dataset: any): void {
        let { label } = currentNode;
        let nodeValue;
        let fullPath;
        let tooltip;
        let parentValue;
        if (path.length > 0) {
            fullPath = `${SEPARATOR}${path.join(SEPARATOR)}${SEPARATOR}${label}`;
            tooltip = `${[...path].join(', ')}, ${label}`;
            parentValue = `${SEPARATOR}${path.join(SEPARATOR)}`;
        } else {
            fullPath = `${SEPARATOR}${label}`;
            tooltip = label;
        }

        // empty names and tooltips causes regressions so we change them to blank strings (see JS-65082)
        if (label === '' || label === null) {
            label = ' ';
        }
        if (tooltip === '') {
            tooltip = ' ';
        }

        const newRowNames: string[] = [...rowNames, getOutputParamsKeyFromField(dataset.summary.rows.items[level])];
        const newPath = [...path, label];
        const nodeDefinition: any = {
            id: fullPath,
            name: label,
            measureLabel: measure.label,
            tooltipLabel: tooltip,
            linkOutputParams: {
                // the TreeMap has always one measure
                measures: [getOutputParamFromMeasure(measure)],
                values: newRowNames.map((newRowName: string, index: number) => {
                    return {
                        key: newRowName,
                        value: newPath[index]
                    }
                })
            }
        }
        if (currentNode.index !== -1) {
            nodeValue = parseFloat(aggregations[currentNode.index]);
        } else if (currentNode.total) {
            const totalIndex = this.getTotalIndex(currentNode.total);
            if (totalIndex !== -1) {
                nodeValue = parseFloat(aggregations[totalIndex]);
            }
        }
        if (parentValue) {
            nodeDefinition.parent = parentValue;
        }
        if (nodeValue) {
            nodeDefinition.value = nodeValue;
        }
        results[level].push(nodeDefinition);
        currentNode.children.forEach((child: any) => {
            this.exploreSeries(results, level + 1, child, newPath, newRowNames, aggregations, measure, dataset);
        });
    }

    finalOptionAdjustment(options: any) {

        const newOptions = { ...options };

        delete newOptions.xAxis;
        delete newOptions.yAxis;

        return super.finalOptionAdjustment(newOptions);
    }
}

export const TreeMapVisualizationDescriptor = {
    legacyAdhoc: 'tree_map',
    parentClass: 'Range',
    cssClass: 'TreeMap',
    bundleName: 'adhoc.visualisation.chooser.type.TREE_MAP',
    name: 'TreeMap',
    group: 'adhoc.visualisation.chooser.type.GROUP_RANGE',
    datasetType: datasetTypes.MULTI_AXIS,
    component: TreeMapVisualization,
    staticPropertiesReducer: genericChartVisualizationStaticPropertiesReducer,
    requirements: {
        measures: {
            min: 1,
            max: 1,
            inRow: false
        },
        fields: {
            min: 1,
            inColumn: false
        }
    }
};
